// Admin app shell: AdminGate + AdminSyncProvider + Router.

import { ErrorBoundary, Han, LoadingSplash, OfflineIndicator, T } from "@goodbye/ui";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AdminAuthContext, AdminSyncContext } from "./context";
import { AdminNav } from "./routes/_AdminNav";
import { env } from "./env";
import { useAdminAuth } from "./hooks/useAdminAuth";
import { useAdminSync } from "./hooks/useAdminSync";
import { useInstallPrompt } from "./hooks/useInstallPrompt";
import { reportError, setSentryUser } from "./sentry";
import { AudioNew } from "./routes/AudioNew";
import { AudioRelease } from "./routes/AudioRelease";
import { EchoesOverview } from "./routes/EchoesOverview";
import { InstallPrompt } from "./routes/InstallPrompt";
import { FragmentList } from "./routes/FragmentList";
import { FragmentNew } from "./routes/FragmentNew";
import { MapMark } from "./routes/MapMark";
import { MessageList } from "./routes/MessageList";
import { MessageNew } from "./routes/MessageNew";
import { RegionsOpen } from "./routes/RegionsOpen";
import { SignIn } from "./routes/SignIn";
import { UsersOverview } from "./routes/UsersOverview";
import { Welcome } from "./routes/Welcome";

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        background: T.paper,
        padding: 40,
        textAlign: "center",
      }}
    >
      <Han size={14} color={T.ink500} lh={1.8}>
        {children}
      </Han>
    </div>
  );
}

function AdminGate({ children }: { children: React.ReactNode }) {
  // PR-C (D11+D12): mobile worker in Safari tab / Chrome → show install
  // overlay first. Desktop / preview / standalone bypass automatically.
  const install = useInstallPrompt();
  const auth = useAdminAuth();

  if (install.state !== "not_needed") {
    return (
      <InstallPrompt
        state={install.state}
        onAndroidInstall={install.triggerNative}
      />
    );
  }

  if (auth.status === "loading")
    return (
      <Splash>
        <LoadingSplash minHeight={0} timeoutMs={12000} />
      </Splash>
    );
  if (auth.status === "needs-signin") return <SignIn />;
  if (auth.status === "not-worker")
    return <SignIn rejectedEmail={auth.email} />;
  if (auth.status === "error")
    return <Splash>{auth.error?.message ?? "登入失敗"}</Splash>;

  setSentryUser(auth.email ?? null);

  return <AdminAuthContext.Provider value={auth}>{children}</AdminAuthContext.Provider>;
}

function RouteBoundary({
  scope,
  children,
}: {
  scope: string;
  children: React.ReactNode;
}) {
  return (
    <ErrorBoundary
      scope={scope}
      onError={(err, info) =>
        reportError(err, { route: scope, componentStack: info.componentStack })
      }
    >
      {children}
    </ErrorBoundary>
  );
}

function AdminSyncProvider({ children }: { children: React.ReactNode }) {
  const sync = useAdminSync();
  return <AdminSyncContext.Provider value={sync}>{children}</AdminSyncContext.Provider>;
}

// 2026-05-30:AdminNav 提升到 routes 外層(跟 PreviewAsLearnerLink 同層,
// 在每頁的 flex 容器之外)。原本各頁把 <AdminNav/> 放在自己的 display:flex
// 容器內,iOS Safari 會把 fixed 子元素誤當 flex item、捲動時 tab「卡在中間」
// 不貼底。提升後 AdminNav 只有 #root/Provider 祖先,fixed 永遠貼底。
// Welcome 頁不顯示 tab(它是登入後的一次性歡迎、非主功能頁)。
function AdminNavGlobal() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/welcome")) return null;
  return <AdminNav />;
}

function PreviewAsLearnerLink() {
  return (
    <a
      href={`${env.userOrigin}/?preview=true`}
      target="_blank"
      rel="noreferrer"
      style={{
        position: "fixed",
        bottom: 8,
        right: 14,
        fontFamily: T.mono,
        fontSize: 9,
        color: T.ink350,
        letterSpacing: "0.18em",
        textDecoration: "none",
        zIndex: 1000,
      }}
    >
      · 以學員身分預覽 ↗
    </a>
  );
}

export function App() {
  return (
    <ErrorBoundary
      scope="Admin"
      onError={(err, info) =>
        reportError(err, { scope: "root", componentStack: info.componentStack })
      }
    >
      <BrowserRouter>
        <AdminGate>
          <AdminSyncProvider>
            <OfflineIndicator />
            <PreviewAsLearnerLink />
            <Routes>
              {/* D16: worker logged-in → straight to /audio, skip Welcome */}
              <Route path="/" element={<Navigate to="/audio" replace />} />
              <Route
                path="/welcome"
                element={<RouteBoundary scope="Welcome"><Welcome /></RouteBoundary>}
              />
              <Route
                path="/audio"
                element={<RouteBoundary scope="AudioRelease"><AudioRelease /></RouteBoundary>}
              />
              <Route
                path="/audio/new"
                element={<RouteBoundary scope="AudioNew"><AudioNew /></RouteBoundary>}
              />
              <Route
                path="/messages"
                element={<RouteBoundary scope="MessageList"><MessageList /></RouteBoundary>}
              />
              <Route
                path="/messages/new"
                element={<RouteBoundary scope="MessageNew"><MessageNew /></RouteBoundary>}
              />
              <Route
                path="/fragments"
                element={<RouteBoundary scope="FragmentList"><FragmentList /></RouteBoundary>}
              />
              <Route
                path="/fragments/new"
                element={<RouteBoundary scope="FragmentNew"><FragmentNew /></RouteBoundary>}
              />
              <Route
                path="/map"
                element={<RouteBoundary scope="MapMark"><MapMark /></RouteBoundary>}
              />
              <Route
                path="/users"
                element={<RouteBoundary scope="UsersOverview"><UsersOverview /></RouteBoundary>}
              />
              <Route
                path="/regions"
                element={<RouteBoundary scope="RegionsOpen"><RegionsOpen /></RouteBoundary>}
              />
              <Route
                path="/echoes"
                element={<RouteBoundary scope="EchoesOverview"><EchoesOverview /></RouteBoundary>}
              />
              <Route path="*" element={<Navigate to="/welcome" replace />} />
            </Routes>
            <AdminNavGlobal />
          </AdminSyncProvider>
        </AdminGate>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
